/* 
规格筛选的外层封装，用于其他页面引入加购/购买的弹窗
*/

import React, { ReactElement, useState, useEffect, useRef } from 'react';
import { $k18n } from '@/common/i18n';
// 样式
import { getEncryptionFormat, getBuyShopStatus, postAddToCart, claimSelfMultiBuildCoupon, PageRowData, useGetDetailDataSimple } from '../../../api';
import SpecLinePop from '../SpecLinePop';
import yoda from '@kibt/bridge';
import { radar } from '@/common/log';
import { onForbidWebDrag } from '@/common/hooks';
import { isInApp } from '@/common/device-info';
import queryString from 'query-string';
import { allowDefaultOutsideClick, ignoreOutsideClick, onClickContainerOutside } from '@/common/utils/clickOnContinerOutSide';
import { getPageProps } from '@kibt/krn-utils';
import { Toast } from '@kibt/meta-ui-krn';
import { useQuery } from '@/hooks/useQuery';
import { useExtParamsObject } from '../../utils';
import { useJumpToConfirmOrder } from '../../../jump';
import { ActivityType } from '../Exclusive/CaculateBlock';
import { useSubsidy } from '../../hooks/useSubsidy';
import { delay } from '@/utils/delay';

export type SpecLinePopWrapProps = {
    itemId: number | string;
    item?: any;
    type: 'cart' | 'buy';
    showPop: boolean;
    extParamsCustom?: {
        cId?: string;
        cT: string;
        cS?: string;
    };
    addToCartLog?: (params) => void;
    addToCartAnimation?: Function;
    onAddToCartSuccessfullyCb?: Function;
    closePop?: Function;
    toastContent?: string;
    setToastCouponConfig?: Function | false;
    merchantId?: number;
    addToCartCallBack?: Function;
    ifForbidWebDrag?: boolean;
    autoBottomGap?: boolean;
    thresholdValue?: number;
    customDuration?: number;
    needErrorClose?: boolean;
    // 是否要打开凑单页
    shouldGoToAddonHelper?: (sku, quantity, before?: () => Promise<boolean>) => Promise<boolean>;
    showCartAndCheckout?: boolean;
    onSkuChange?: (sku) => void;
    onPurchaseQuantityChange?: (count) => void;
    popupStyle?: any;
    addToCartApiLog?: (params) => void;
    showAfterCouponPrice?: boolean;
    // 是否打开半屏提单
    openHalfConfirmOrder?: boolean;
    // 规格弹窗是否需要商品详情入口
    showDetailUrl?: string | boolean;
    isFrom?: string;
    scene?: number | string; // 发券场景值
    sceneId?: number; // 交易场景值
    noNeedFetchBuyNowStatus?: boolean; // 是否需要请求商品状态的接口 为了让加购出来的更快
    sellerMerchantId?: string;
    isReco?: boolean;
    isSpecPage?: boolean;
};

/**
 * 规格弹窗
 */
function SpecLinePopWrap(props: SpecLinePopWrapProps): ReactElement {
    const skuRef = useRef(null);
    const purchaseQuantityRef = useRef(1);
    const [customShowPop, setCustomShowPop] = useState(false);

    const [pageRawData, setPageRawData] = useState({} as PageRowData);

    const {
        itemId,
        item,
        type = 'cart',
        showPop = false,
        closePop,
        toastContent,
        addToCartAnimation,
        addToCartCallBack,
        ifForbidWebDrag = true,
        autoBottomGap = false,
        customDuration,
        needErrorClose,
        showCartAndCheckout,
        popupStyle,
        showAfterCouponPrice = false,
        openHalfConfirmOrder = false,
        isFrom,
        noNeedFetchBuyNowStatus = false,
        isReco = false,
        isSpecPage = false,
    } = props;

    // 达人uid
    let sellerUserIdInQuery = useQuery('sellerUserId') as string;
    let sellerMerchantIdInQuery = useQuery('merchantId') as string;

    // 特殊处理：如果是直播间小黄车/双列等场景的列表加购，可以取item中jumpUrl里的分销信息
    if (item && item.jumpUrl) {
        const parseUrl = queryString.parseUrl(item.jumpUrl);
        sellerUserIdInQuery = (parseUrl.query.sellerUserId as string) || '';
        sellerMerchantIdInQuery = (parseUrl.query.merchantId as string) || '';
    }

    if (props.sellerMerchantId) {
        sellerMerchantIdInQuery = props.sellerMerchantId;
    }

    // 需要凑单
    const shouldGoToAddonHelper = async (before?: () => Promise<boolean>) => {
        return props.shouldGoToAddonHelper?.(skuRef.current, purchaseQuantityRef.current, before) ?? Promise.resolve(false);
    };

    // 按钮吸顶适配
    const extParams = useQuery('extParams') as string;
    const extParamsObj = useExtParamsObject();
    // 按钮吸底适配
    // 只有当半屏可拖动全屏容器时，才需要适配吸底，因为此时存在容器在ios屏幕下面
    // 注意，当客户端打开全屏拖半屏容器时，我们拿不到这两个openSlideFullScreen和foldRatio参数
    const foldRatio = useQuery('openSlideFullScreen') === '1' ? (useQuery('foldRatio') as string) : 0;

    const minBuyNum = useRef(1);

    // 能够加购
    const buyNowStatus = async (itemId, skuId) => {
        let flag = true;
        const ext = extParams || '';
        await getBuyShopStatus({ itemId, skuId, extParams: ext })
            .then((res) => {
                if (
                    res.data.shopStatus === 2 ||
                    res.data.shopBusinessStatus === 2 ||
                    res.data.stock <= 0 ||
                    res.data.activeStatus !== 2 ||
                    res.data.auditStatus !== 2
                ) {
                    flag = false;
                    if (res.data.shopBusinessStatus === 1 && res.data.shopStatus === 1) {
                        if (res.data.activeStatus !== 2 && res.data.auditStatus === 2) {
                            Toast.show({
                                text: $k18n('抱歉，商品已下架'),
                                duration: 3000,
                            });
                        } else if (res.data.activeStatus === 2 && res.data.auditStatus === 2 && res.data.stock <= 0) {
                            Toast.show({
                                text: $k18n('抱歉，商品已售罄'),
                                duration: 3000,
                            });
                        } else {
                            Toast.show({
                                text: $k18n('糟糕！商品找不到啦'),
                                duration: 3000,
                            });
                        }
                    } else {
                        Toast.show({
                            text: $k18n('糟糕！商品找不到啦'),
                            duration: 3000,
                        });
                    }
                }
            })
            .catch((err) => {
                flag = false;
            });

        return flag;
    };
    const closeSpec = () => {
        setPageRawData({} as PageRowData);
        !isSpecPage && allowDefaultOutsideClick();
        closePop?.();
    };
    const postAddCartItem = async (sku, num = 1, sellerMerchantId?, callShouldGoToAddonHelper = false) => {
        // react时序问题，直接setState后使用pageRawData拿不到值，这里改为传入

        props.addToCartApiLog?.(sku);

        // 未登陆需要登陆
        const res = await yoda.component.login({});
        if (res && +res.result === 1) {
            try {
                // 调用加入购物车接口
                const res = await postAddToCart({
                    itemId: itemId,
                    skuQuantity: num || 1,
                    merchantId: sku.merchantId,
                    skuId: sku.skuId,
                    // 埋点来源
                    mediaId: props.extParamsCustom?.cId ?? extParamsObj.cId,
                    mediaSource: props.extParamsCustom?.cT ?? extParamsObj.cT,
                    mediaDetailSource: props.extParamsCustom?.cS ?? extParamsObj.cS,
                    sellerMerchantId: sellerMerchantId || pageRawData?.sellerMerchantShop?.merchantId,
                    recoDetail: {
                        expTag: item?.retrievalInfo?.expTag,
                        llsid: item?.retrievalInfo?.llsid,
                    },
                    sceneId: Number(props.sceneId || 0),
                });
                if (addToCartCallBack) {
                    addToCartCallBack();
                }
                if (res.result === 1) {
                    radar.event({
                        name: 'SHOP_ADD_TO_CART',
                        params: {
                            skuId: sku.skuId,
                            media_source: props.extParamsCustom?.cT ?? extParamsObj.cT,
                            sellerMerchantId: sellerMerchantId || pageRawData?.sellerMerchantShop?.merchantId,
                        },
                    });
                    closeSpec();
                    props.onAddToCartSuccessfullyCb?.(res);
                    const getSuccessToast = () => {
                        // 购物车猜你喜欢的加购数量等于最小起购数量时，toast提示“x件起购，已成功加购x件”
                        if (getPageProps()?.bundleId === 'MyShoppingCar' && minBuyNum.current > 1 && num === minBuyNum.current) {
                            return $k18n({ id: 'k_452576', text: '{x}件起购，已成功加购{y}件，请稍后下拉刷新', params: { x: num, y: num } });
                        }
                        // 加购数量等于最小起购数量时，toast提示“x件起购，已成功加购x件”
                        if (minBuyNum.current > 1 && num === minBuyNum.current) {
                            return $k18n({ id: 'k_450489', text: '{x}件起购，已成功加购{y}件', params: { x: num, y: num } });
                        }
                        return toastContent || $k18n('添加成功');
                    };
                    const successToast = getSuccessToast();
                    Toast.show({
                        text: successToast,
                        duration: 1500,
                    });
                    setTimeout(() => {
                        // 加购成功后打开凑单半屏
                        callShouldGoToAddonHelper && shouldGoToAddonHelper?.();
                    }, 1500);
                    return true;
                } else {
                    closeSpec();
                    if (needErrorClose) {
                        closeSpec();
                    }
                    return false;
                }
            } catch (err) {
                closeSpec();
                if (needErrorClose) {
                    closeSpec();
                }
                return false;
            }
        }
    };

    const { jumpToConfirmOrder } = useJumpToConfirmOrder();
    const { hasSubsidy } = useSubsidy(pageRawData?.skus?.[0], pageRawData);
    const onJumpToConfirmOrder = async (pageRawData, addressId, buyamount, merchantId, skuId, itemId, poc, needDelay1 = false, sku: any) => {
        const needDelay = false;
        // 未登陆需要登陆
        const res = await yoda.component.login({});
        if (res && +res.result === 1) {
            console.log('pageRawData', pageRawData);
            const shouldOpenAddonHelper = await shouldGoToAddonHelper(() =>
                postAddCartItem(sku, buyamount, pageRawData?.sellerMerchantShop?.merchantId, false),
            );
            if (shouldOpenAddonHelper) {
                return;
            }
            const subsidyItem = pageRawData?.activityList?.find((v) => v?.marketingActivityType === ActivityType.Exclusive);

            const subsidyParams =
                hasSubsidy && subsidyItem
                    ? {
                          promotionId: subsidyItem.activityId,
                          afterDiscountPrice: sku?.activityPrice ?? pageRawData.activityPrice,
                      }
                    : {};
            // 自动领取未领取的优惠券
            if (pageRawData.discountCoupon && pageRawData.discountCoupon.length) {
                try {
                    const claimCoupons =
                        pageRawData.discountCoupon?.filter((_coupon) => _coupon.autoClaimable).map((_coupon) => _coupon.batchId) || [];
                    if (claimCoupons.length) {
                        const res = await claimSelfMultiBuildCoupon({ batchIds: claimCoupons.join(',') });
                        console.log('claimSelfBuildCoupon', res);
                        if (res.result === 1) {
                            pageRawData.discountCoupon?.map((_coupon) => {
                                if (claimCoupons.includes(_coupon.batchId)) _coupon.couponState = 1;
                            });
                            await Toast.show({
                                text: $k18n({ text: '已成功帮您领取优惠券，快来下单', id: 'k_445554' }),
                                duration: 1000,
                            });
                            const sellerMerchantId = (pageRawData.sellerMerchantShop && pageRawData.sellerMerchantShop.merchantId) || 0;
                            const flashSaleActivityId =
                                pageRawData?.activityInfo?.activityId &&
                                pageRawData?.activityInfo?.status === 2 &&
                                pageRawData?.activityInfo?.saleRate < 100
                                    ? pageRawData?.activityInfo?.activityId
                                    : 0;
                            await jumpToConfirmOrder({
                                addressId,
                                buyamount: Math.max(buyamount, pageRawData.minPurchase),
                                merchantId,
                                skuId,
                                itemId,
                                sellerMerchantId,
                                poc,
                                needDelay,
                                flashSaleActivityId,
                                minPurchase: pageRawData.minPurchase,
                                halfWebview: openHalfConfirmOrder,
                                retrievalInfo: '{}',
                                extParamsCustom: props.extParamsCustom,
                                ...subsidyParams,
                                // backendDispatchUrl: pageRawData?.pageUrl?.placeOrderJumpUrl, // simpleInfo本期没有返回下发链接
                            });
                        }
                    } else {
                        const sellerMerchantId = (pageRawData.sellerMerchantShop && pageRawData.sellerMerchantShop.merchantId) || 0;
                        const flashSaleActivityId =
                            pageRawData?.activityInfo?.activityId &&
                            pageRawData?.activityInfo?.status === 2 &&
                            pageRawData?.activityInfo?.saleRate < 100
                                ? pageRawData?.activityInfo?.activityId
                                : 0;
                        await jumpToConfirmOrder({
                            addressId,
                            buyamount: Math.max(buyamount, pageRawData.minPurchase),
                            merchantId,
                            skuId,
                            itemId,
                            sellerMerchantId,
                            poc,
                            needDelay,
                            flashSaleActivityId,
                            minPurchase: pageRawData.minPurchase,
                            halfWebview: openHalfConfirmOrder,
                            retrievalInfo: '{}',
                            extParamsCustom: props.extParamsCustom,
                            ...subsidyParams,
                            // backendDispatchUrl: pageRawData?.pageUrl?.placeOrderJumpUrl, // simpleInfo本期没有返回下发链接
                        });
                    }
                } catch (e) {
                    console.log('报错', e);
                    const sellerMerchantId = (pageRawData.sellerMerchantShop && pageRawData.sellerMerchantShop.merchantId) || 0;
                    const flashSaleActivityId =
                        pageRawData?.activityInfo?.activityId && pageRawData?.activityInfo?.status === 2 && pageRawData?.activityInfo?.saleRate < 100
                            ? pageRawData?.activityInfo?.activityId
                            : 0;
                    await jumpToConfirmOrder({
                        addressId,
                        buyamount: Math.max(buyamount, pageRawData.minPurchase),
                        merchantId,
                        skuId,
                        itemId,
                        sellerMerchantId,
                        poc,
                        needDelay,
                        flashSaleActivityId,
                        minPurchase: pageRawData.minPurchase,
                        halfWebview: openHalfConfirmOrder,
                        retrievalInfo: '{}',
                        extParamsCustom: props.extParamsCustom,
                        ...subsidyParams,
                        // backendDispatchUrl: pageRawData?.pageUrl?.placeOrderJumpUrl, // simpleInfo本期没有返回下发链接
                    });
                }
            } else {
                const sellerMerchantId = (pageRawData.sellerMerchantShop && pageRawData.sellerMerchantShop.merchantId) || 0;
                const flashSaleActivityId =
                    pageRawData?.activityInfo?.activityId && pageRawData?.activityInfo?.status === 2 && pageRawData?.activityInfo?.saleRate < 100
                        ? pageRawData?.activityInfo?.activityId
                        : 0;
                await jumpToConfirmOrder({
                    addressId,
                    buyamount: Math.max(buyamount, pageRawData.minPurchase),
                    merchantId,
                    skuId,
                    itemId,
                    sellerMerchantId,
                    poc,
                    needDelay,
                    flashSaleActivityId,
                    minPurchase: pageRawData.minPurchase,
                    halfWebview: openHalfConfirmOrder,
                    retrievalInfo: '{}',
                    extParamsCustom: props.extParamsCustom,
                    ...subsidyParams,
                    // backendDispatchUrl: pageRawData?.pageUrl?.placeOrderJumpUrl, // simpleInfo本期没有返回下发链接
                });
            }
        }
    };
    const { getDetailDataSimple, data: simpleDetail } = useGetDetailDataSimple({
        itemId,
        encryptedParam: JSON.stringify(getEncryptionFormat()), // 不能JSON.stringify, 预请求参数需要和这里一致
        sellerUserId: Number(sellerUserIdInQuery || 0),
        merchantId: Number(sellerMerchantIdInQuery || 0),
        scene: props.scene || 0,
    });

    useEffect(() => {
        const refreshData = async () => {
            if (simpleDetail?.data) {
                const detailDataResData = simpleDetail.data;
                console.log('detailDataResData', detailDataResData.sellerMerchantShop);
                setPageRawData(detailDataResData);
                // 最小起购数量
                minBuyNum.current = simpleDetail?.data?.minPurchase || 1;
                // 单规格直接加购，不用弹出规格弹窗
                if (detailDataResData.skus.length === 1) {
                    const sku = detailDataResData.skus[0];
                    const skuId = sku.skuId;
                    let shopFlag;
                    if (detailDataResData?.extraInfo?.selfOperated || noNeedFetchBuyNowStatus) {
                        shopFlag = true;
                    } else {
                        shopFlag = await buyNowStatus(itemId, skuId);
                    }

                    if (shopFlag) {
                        // 区分当前弹窗是加购还是购买
                        if (props.type === 'cart') {
                            // 触发外部按钮点击的埋点，add_type // 0-吊起规格页，1-加购成功
                            props?.addToCartLog?.({
                                item_id: itemId,
                                sku_id: skuId,
                                addType: 1,
                            });
                            const result = await postAddCartItem(sku, minBuyNum.current, detailDataResData?.sellerMerchantShop?.merchantId, true);
                            if (result) {
                                addToCartAnimation?.();
                            }
                        } else if (props.type === 'buy') {
                            // 直接购买点击，跳转确认订单页面
                            onJumpToConfirmOrder(
                                detailDataResData,
                                '',
                                minBuyNum.current,
                                detailDataResData.merchantId,
                                skuId,
                                itemId,
                                'RecommendGoos',
                                true,
                                sku,
                            ).finally(async () => {
                                await delay(500);
                                closeSpec();
                            });
                        }
                    } else {
                        closeSpec();
                    }
                } else {
                    setCustomShowPop(true);
                    if (props.type === 'cart') {
                        props?.addToCartLog?.({
                            item_id: itemId,
                            addType: 0,
                        });
                    }
                    !isSpecPage && ignoreOutsideClick();
                    !isSpecPage &&
                        onClickContainerOutside(() => {
                            closeSpec();
                        });
                }
            }
        };
        refreshData();
    }, [simpleDetail]);

    const singleSkuAddCart = async () => {
        if (props.type === 'cart') {
            // 触发外部按钮点击的埋点，add_type // 0-吊起规格页，1-加购成功
            props?.addToCartLog?.({
                item_id: itemId,
                sku_id: item?.skuIdList?.[0],
                addType: 1,
            });

            try {
                // 调用加入购物车接口
                const res = await postAddToCart({
                    itemId: itemId,
                    skuQuantity: item?.minPurchase || 1,
                    merchantId: item?.merchantId,
                    skuId: item?.skuIdList?.[0],
                    // 埋点来源
                    mediaId: props.extParamsCustom?.cId ?? extParamsObj.cId,
                    mediaSource: props.extParamsCustom?.cT ?? extParamsObj.cT,
                    mediaDetailSource: props.extParamsCustom?.cS ?? extParamsObj.cS,
                    sellerMerchantId: props.sellerMerchantId,
                    recoDetail: {
                        expTag: item?.retrievalInfo?.expTag,
                        llsid: item?.retrievalInfo?.llsid,
                    },
                    sceneId: Number(props.sceneId || 0),
                });
                if (addToCartCallBack) {
                    addToCartCallBack();
                }
                if (res.result === 1) {
                    radar.event({
                        name: 'SHOP_ADD_TO_CART',
                        params: {
                            skuId: item?.skuIdList?.[0],
                            media_source: props.extParamsCustom?.cT ?? extParamsObj.cT,
                            sellerMerchantId: props.sellerMerchantId,
                        },
                    });
                    closeSpec();
                    props.onAddToCartSuccessfullyCb?.(res);
                    Toast.show({ text: $k18n('添加成功') });
                    return true;
                } else {
                    closeSpec();
                    return false;
                }
            } catch (error) {
                closeSpec();
            }
        }
    };

    useEffect(() => {
        setCustomShowPop(false);
        if (isInApp && ifForbidWebDrag) {
            onForbidWebDrag(showPop);
        }
        if (itemId && showPop) {
            console.log('isReco setCustomShowPop', isReco);
            if (item?.skuIdList?.length === 1 && isFrom === 'merchant') {
                console.log('准备直接加购');
                singleSkuAddCart();
                return;
            }
            if (isFrom === 'shoppingCar' && item.skuId && props.type === 'cart') {
                console.log('准备直接加购');
                getDetailDataSimple();
                return;
            }
            if (isReco) {
                console.log('无法感知是否是单sku，防止闪动，先不展示popup');
                getDetailDataSimple();
                return;
            }
            if (isFrom === 'recommendGoods-minCard' && item.skus?.length === 1) {
                getDetailDataSimple();
                return;
            }
            simpleDetail?.data?.itemId === itemId && setPageRawData(simpleDetail?.data);
            getDetailDataSimple();
            setCustomShowPop(true);
        }
    }, [showPop, itemId]);

    return (
        customShowPop && (
            <SpecLinePop
                type={type}
                isPopVisible={showPop ? customShowPop : null} // 当showPop和customShowPop同时满足时才展示,主要是在单规格时处理此逻辑
                pageRawData={pageRawData}
                closeSpec={closeSpec}
                addCartItem={postAddCartItem}
                foldRatio={foldRatio}
                showAfterCouponPrice={showAfterCouponPrice}
                customDuration={customDuration}
                addToCartAnimation={addToCartAnimation}
                autoBottomGap={autoBottomGap}
                shouldGoToAddonHelper={shouldGoToAddonHelper}
                showCartAndCheckout={showCartAndCheckout}
                popupStyle={popupStyle}
                openHalfConfirmOrder={openHalfConfirmOrder}
                showDetailUrl={props.showDetailUrl}
                isFrom={isFrom}
                extParamsCustom={props.extParamsCustom}
                isReco={isReco}
                isSpecPage={isSpecPage}
                scene={props.scene}
            />
        )
    );
}

export default SpecLinePopWrap;
